import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  User, Lock, LogIn, UserPlus, HelpCircle, Eye, EyeOff,
  FileText, ChevronRight, Users, TrendingUp, Award, Star, Moon, Sun, Send
} from 'lucide-react';
import SoporteModal from '../components/SoporteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URLs
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_444ea87a-9e43-44f5-894f-5ab28ef3d9aa/artifacts/9vynnk18_Captura%20de%20Pantalla%202026-04-22%20a%20la%28s%29%203.31.11%20p.%C2%A0m..png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_444ea87a-9e43-44f5-894f-5ab28ef3d9aa/artifacts/36k2ajjp_Captura%20de%20Pantalla%202026-04-22%20a%20la%28s%29%202.36.04%20p.%C2%A0m..png";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [soporteOpen, setSoporteOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      login(response.data.access_token, response.data.user);
      toast.success('¡Bienvenido de nuevo!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      title: '¿Qué es el Registro Único?',
      icon: FileText,
      heading: 'Una plataforma de acceso centralizado',
      description: 'El Registro Único es el sistema oficial de iNNpulsa Colombia que unifica la información de empresas y emprendedores para acceder a programas de apoyo, convocatorias y recursos del ecosistema de emprendimiento e innovación del país.',
      points: [
        'Centraliza tu información empresarial en un solo lugar',
        'Válido para todas las convocatorias de iNNpulsa',
        'Gratuito y de acceso permanente',
        'Respaldado por el Ministerio de Comercio, Industria y Turismo'
      ]
    },
    {
      title: '¿Cómo registrarse?',
      icon: UserPlus,
      heading: 'Proceso de registro sencillo',
      description: 'Registrarte en el sistema es fácil y rápido. Solo necesitas tu información básica y podrás acceder a todas las convocatorias disponibles.',
      points: [
        'Haz clic en "Crear cuenta nueva"',
        'Selecciona si eres Persona Natural o Jurídica',
        'Completa el formulario con tus datos',
        'Verifica tu correo electrónico y listo'
      ]
    },
    {
      title: '¿Cómo postularse?',
      icon: Send,
      heading: 'Accede a convocatorias activas',
      description: 'Con tu registro activo puedes postularte a cualquier programa o convocatoria vigente. El sistema te guía paso a paso durante todo el proceso de postulación.',
      points: [
        'Consulta las convocatorias abiertas desde tu panel',
        'Verifica los requisitos y criterios de elegibilidad',
        'Completa el formulario de postulación en línea',
        'Realiza seguimiento en tiempo real a tu postulación'
      ]
    }
  ];

  const stats = [
    { icon: Users, value: '62.800', label: 'Personas naturales', color: 'red' },
    { icon: TrendingUp, value: '14.500', label: 'Empresas registradas', color: 'blue' },
    { icon: Award, value: '+210', label: 'Convocatorias realizadas', color: 'yellow' },
    { icon: Star, value: '32', label: 'Departamentos', color: 'green' },
    { icon: Star, value: '1.103', label: 'Municipios', color: 'orange' }
  ];

  // Light mode colors
  const lightColors = {
    red: { bg: 'bg-red-50', border: 'border-red-100', iconColor: 'text-red-600', valueColor: 'text-red-700', hover: 'hover:bg-red-100' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', iconColor: 'text-blue-700', valueColor: 'text-blue-800', hover: 'hover:bg-blue-100' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-100', iconColor: 'text-yellow-600', valueColor: 'text-yellow-700', hover: 'hover:bg-yellow-100' },
    green: { bg: 'bg-green-50', border: 'border-green-100', iconColor: 'text-green-600', valueColor: 'text-green-700', hover: 'hover:bg-green-100' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-100', iconColor: 'text-orange-500', valueColor: 'text-orange-600', hover: 'hover:bg-orange-100' }
  };

  // Dark mode colors - more vibrant
  const darkColors = {
    red: { bg: 'bg-red-950/60', border: 'border-red-800/50', iconColor: 'text-red-400', valueColor: 'text-red-400', hover: 'hover:bg-red-900/60' },
    blue: { bg: 'bg-blue-950/60', border: 'border-blue-800/50', iconColor: 'text-blue-400', valueColor: 'text-blue-400', hover: 'hover:bg-blue-900/60' },
    yellow: { bg: 'bg-yellow-950/60', border: 'border-yellow-800/50', iconColor: 'text-yellow-400', valueColor: 'text-yellow-400', hover: 'hover:bg-yellow-900/60' },
    green: { bg: 'bg-green-950/60', border: 'border-green-800/50', iconColor: 'text-green-400', valueColor: 'text-green-400', hover: 'hover:bg-green-900/60' },
    orange: { bg: 'bg-orange-950/60', border: 'border-orange-800/50', iconColor: 'text-orange-400', valueColor: 'text-orange-400', hover: 'hover:bg-orange-900/60' }
  };

  const colorClasses = darkMode ? darkColors : lightColors;

  return (
    <div className="flex overflow-hidden" style={{ height: '100dvh' }}>
      {/* Left Panel - Info Section (70%) */}
      <div className="hidden lg:flex flex-col" style={{ width: '70%' }}>
        <div 
          className={`relative flex flex-col h-full overflow-hidden transition-colors duration-300`}
          style={{ backgroundColor: darkMode ? '#1a1a1a' : '#ffffff' }}
        >
          {/* Background decorations - only in light mode */}
          {!darkMode && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-red-50 opacity-60"></div>
              <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-yellow-50 opacity-50"></div>
              <div className="absolute top-1/2 right-0 w-64 h-64 rounded-full bg-blue-50 opacity-40"></div>
            </div>
          )}

          <div className="relative z-10 flex flex-col h-full p-8 lg:p-12">
            {/* Logo */}
            <div className="flex items-center mb-12">
              <img 
                src={darkMode ? LOGO_DARK : LOGO_LIGHT}
                alt="Ministerio de Comercio, Industria y Turismo - iNNpulsa Colombia" 
                className="h-14 w-auto object-contain"
              />
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {tabs.map((tab, index) => (
                <button
                  key={tab.title}
                  onClick={() => setActiveTab(index)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 border ${
                    activeTab === index
                      ? 'text-white border-transparent shadow-md'
                      : darkMode 
                        ? 'bg-transparent text-gray-400 border-gray-700 hover:border-gray-500 hover:text-gray-200' 
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
                  }`}
                  style={activeTab === index ? { backgroundColor: darkMode ? '#C41E3A' : '#C41E3A', borderColor: '#C41E3A' } : {}}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 transition-all duration-300">
              <div 
                className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 ${darkMode ? 'bg-yellow-500/20' : 'bg-red-50'}`}
              >
                {React.createElement(tabs[activeTab].icon, { 
                  className: `w-7 h-7 ${darkMode ? 'text-yellow-400' : 'text-red-700'}` 
                })}
              </div>
              
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {tabs[activeTab].heading}
              </h2>
              
              <p className={`text-base leading-relaxed mb-6 max-w-xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {tabs[activeTab].description}
              </p>

              <ul className="space-y-3">
                {tabs[activeTab].points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <ChevronRight 
                      className="w-5 h-5 mt-0.5 flex-shrink-0" 
                      style={{ color: darkMode ? '#facc15' : '#C41E3A' }} 
                    />
                    <span className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {point}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Dots indicator */}
              <div className="flex gap-2 mt-8">
                {tabs.map((tab, index) => (
                  <button
                    key={`dot-${tab.title}`}
                    onClick={() => setActiveTab(index)}
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: activeTab === index ? '32px' : '8px',
                      background: activeTab === index 
                        ? (darkMode ? '#facc15' : '#C41E3A') 
                        : (darkMode ? '#374151' : '#E5E7EB')
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className={`mt-auto pt-8 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <p className={`text-xs uppercase tracking-widest mb-4 font-semibold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                iNNpulsa en cifras
              </p>
              <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
                {stats.map((stat) => {
                  const colors = colorClasses[stat.color];
                  return (
                    <div 
                      key={stat.label}
                      className={`${colors.bg} border ${colors.border} rounded-xl p-4 ${colors.hover} transition-all duration-300`}
                    >
                      <stat.icon className={`w-4 h-4 ${colors.iconColor} mb-2`} />
                      <p className={`${colors.valueColor} font-bold text-lg leading-none mb-1`}>{stat.value}</p>
                      <p className={`text-xs leading-tight ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form (30%) */}
      <div 
        className={`flex flex-col w-full lg:w-[30%] relative border-l ${darkMode ? 'border-gray-800' : 'border-gray-100'}`} 
        style={{ minHeight: '100dvh' }}
      >
        <div 
          className="relative flex flex-col h-full"
          style={{ backgroundColor: darkMode ? '#242424' : '#ffffff' }}
        >
          {/* Dark mode toggle */}
          <div className="absolute top-4 right-4 z-10">
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full transition-all duration-200 ${
                darkMode ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>

          {/* Form Container */}
          <div className="flex flex-col justify-center items-center h-full px-8 py-12">
            <div className="w-full max-w-sm">
              {/* Header with Logo */}
              <div className="mb-8 text-center">
                <div className="inline-flex items-center justify-center mb-5">
                  <img 
                    src={darkMode ? LOGO_DARK : LOGO_LIGHT}
                    alt="iNNpulsa Colombia" 
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <h1 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Registro Único
                </h1>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Ingresa a tu cuenta de iNNpulsa Colombia
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label 
                    htmlFor="email" 
                    className={`block text-xs font-semibold mb-1.5 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <User className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      id="email"
                      type="email"
                      placeholder="usuario@ejemplo.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl text-sm transition-all duration-200 outline-none border-2 placeholder-gray-500 ${
                        darkMode 
                          ? 'bg-[#1a1a1a] text-white border-gray-700 hover:border-gray-600 focus:border-red-500' 
                          : 'bg-gray-50 text-gray-900 border-gray-200 hover:border-gray-300 focus:border-red-500'
                      }`}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label 
                      htmlFor="password" 
                      className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      Contraseña
                    </label>
                    <Link 
                      to="/forgot-password"
                      className="text-xs font-medium text-red-500 hover:text-red-400 transition-colors"
                      data-testid="login-forgot-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full pl-10 pr-11 py-3 rounded-xl text-sm transition-all duration-200 outline-none border-2 placeholder-gray-500 ${
                        darkMode 
                          ? 'bg-[#1a1a1a] text-white border-gray-700 hover:border-gray-600 focus:border-red-500' 
                          : 'bg-gray-50 text-gray-900 border-gray-200 hover:border-gray-300 focus:border-red-500'
                      }`}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors ${
                        darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 mt-2 text-white shadow-md hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ background: 'linear-gradient(135deg, #9B1427 0%, #C41E3A 100%)' }}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Ingresar al sistema
                    </>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
                </div>
                <div className="relative flex justify-center">
                  <span 
                    className={`px-3 text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                    style={{ backgroundColor: darkMode ? '#242424' : '#ffffff' }}
                  >
                    ¿No tienes cuenta?
                  </span>
                </div>
              </div>

              {/* Register Button */}
              <Link
                to="/register"
                className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 border-2 hover:scale-[1.02] active:scale-[0.98] ${
                  darkMode 
                    ? 'border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800/50 hover:text-white' 
                    : 'border-gray-200 text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-700'
                }`}
                data-testid="login-register-link"
              >
                <UserPlus className="w-4 h-4" />
                Crear cuenta nueva
              </Link>

              {/* Help Link */}
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setSoporteOpen(true)}
                  data-testid="open-soporte-btn"
                  className={`w-full flex items-center justify-center gap-2 text-xs font-medium transition-colors ${
                    darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  ¿Necesitas ayuda? Centro de soporte
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div 
            className={`absolute bottom-0 left-0 right-0 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'} px-8 py-4`}
            style={{ backgroundColor: darkMode ? '#242424' : '#ffffff' }}
          >
            <div className="flex flex-col items-center gap-1">
              <p className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                © 2026 iNNpulsa Colombia · MinCIT
              </p>
            </div>
          </div>
        </div>
      </div>
      <SoporteModal open={soporteOpen} onClose={() => setSoporteOpen(false)} />
    </div>
  );
}
